/**
 * ShapePlacementPanel — modal UI for placing a polyomino shape on the grid.
 *
 * Shows the current grid mask as solid tiles with ghost cells at all valid
 * edge positions. Player clicks a ghost cell to place the shape there.
 * For multi-cell shapes, R key rotates. Click confirms placement.
 */

declare const Phaser: any;

import { Cell, cellKey, Shape } from "../../../../framework/geometry";
import { Depth } from "../../../../framework/Depth";
import { Panel } from "../../../../framework/Panel";
import type { ShapeDef } from "../../../character/shapes";
import { GridMask } from "./GridMask";

const GHOST_FILL = 0x22c55e;
const GHOST_ALPHA = 0.25;
const PREVIEW_FILL = 0x22c55e;
const PREVIEW_ALPHA = 0.6;
const EXISTING_FILL = 0x374151;
const EXISTING_ALPHA = 0.5;

export class ShapePlacementPanel {
  private scene: any;
  private mask: GridMask;
  private shapeDef: ShapeDef;
  private onPlace: (mask: GridMask) => void;

  private panel: Panel | null = null;
  private existingTiles: any[] = [];
  private ghostTiles: Map<string, any> = new Map();
  private previewTiles: any[] = [];

  private rotations: Shape[];
  private rotationIndex = 0;
  private hoveredAnchor: Cell | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  // Preview rendering params
  private tileSize = 0;
  private tileGap = 3;
  private originX = 0;
  private originY = 0;
  private baseRow = 0;
  private baseCol = 0;

  constructor(
    scene: any,
    mask: GridMask,
    shapeDef: ShapeDef,
    onPlace: (mask: GridMask) => void,
  ) {
    this.scene = scene;
    this.mask = mask;
    this.shapeDef = shapeDef;
    this.onPlace = onPlace;
    this.rotations = shapeDef.shape.allRotations();
  }

  show(): void {
    const panelW = 360;
    const panelH = 280;

    this.panel = new Panel(this.scene, {
      x: 400, y: 225,
      width: panelW, height: panelH,
      dim: true, dimAlpha: 0.6,
      fadeDuration: 200,
    });

    this.panel.addText(0, -panelH / 2 + 20, `Place: ${this.shapeDef.name}`, {
      fontSize: "16px", color: "#22c55e", fontStyle: "bold",
    });

    if (this.rotations.length > 1) {
      this.panel.addText(0, -panelH / 2 + 38, "Press R to rotate", {
        fontSize: "11px", color: "#9ca3af",
      });
    }

    // Compute preview layout — figure out the bounding box including possible placements
    this.computePreviewLayout(panelW, panelH);

    // Draw existing mask cells
    this.drawExistingCells();

    // Draw ghost cells for current rotation
    this.drawGhosts();

    this.panel.show();

    // Keyboard listener for rotation
    if (this.rotations.length > 1) {
      this.keyHandler = (e: KeyboardEvent) => {
        if (e.key === "r" || e.key === "R") {
          this.rotationIndex = (this.rotationIndex + 1) % this.rotations.length;
          this.clearGhosts();
          this.clearPreview();
          this.drawGhosts();
        }
      };
      window.addEventListener("keydown", this.keyHandler);
    }
  }

  private computePreviewLayout(panelW: number, panelH: number): void {
    const shape = this.rotations[0];
    const b = this.mask.bounds();
    // Expand bounds by 1 in each direction to show ghost area
    const pad = Math.max(shape.height, shape.width, 1);
    const minR = b.minRow - pad;
    const maxR = b.maxRow + pad;
    const minC = b.minCol - pad;
    const maxC = b.maxCol + pad;
    const rows = maxR - minR + 1;
    const cols = maxC - minC + 1;

    const availW = panelW - 40;
    const availH = panelH - 70;
    const fromW = Math.floor((availW + this.tileGap) / cols - this.tileGap);
    const fromH = Math.floor((availH + this.tileGap) / rows - this.tileGap);
    this.tileSize = Math.max(12, Math.min(28, fromW, fromH));

    const totalW = cols * (this.tileSize + this.tileGap) - this.tileGap;
    const totalH = rows * (this.tileSize + this.tileGap) - this.tileGap;
    this.originX = 400 - totalW / 2;
    this.originY = 225 - totalH / 2 + 15; // offset down slightly for title
    this.baseRow = minR;
    this.baseCol = minC;
  }

  private cellToPixel(cell: Cell): { x: number; y: number } {
    const stride = this.tileSize + this.tileGap;
    return {
      x: this.originX + (cell.col - this.baseCol) * stride + this.tileSize / 2,
      y: this.originY + (cell.row - this.baseRow) * stride + this.tileSize / 2,
    };
  }

  private drawExistingCells(): void {
    for (const cell of this.mask.toArray()) {
      const { x, y } = this.cellToPixel(cell);
      const rect = this.scene.add.rectangle(x, y, this.tileSize, this.tileSize, EXISTING_FILL, EXISTING_ALPHA);
      rect.setStrokeStyle(1, 0x4b5563);
      rect.setDepth(Depth.OVERLAY_CONTENT);
      this.existingTiles.push(rect);
      this.panel!.addRaw(rect);
    }
  }

  private drawGhosts(): void {
    const shape = this.rotations[this.rotationIndex];
    const placements = this.mask.validPlacements(shape, 10);

    // For each valid anchor, collect all cells the shape would occupy
    // But for click targets, we use the anchor position
    for (const anchor of placements) {
      // For single-cell shapes, ghost IS the cell. For multi-cell, show anchor dot.
      const shapeCells = shape.cells.map(c => ({
        row: c.row + anchor.row,
        col: c.col + anchor.col,
      }));

      // Draw all cells of the shape at this position as ghost outlines
      for (const sc of shapeCells) {
        const key = cellKey(sc);
        if (this.ghostTiles.has(key)) continue; // already drawn by another placement
        const { x, y } = this.cellToPixel(sc);
        const rect = this.scene.add.rectangle(x, y, this.tileSize, this.tileSize, GHOST_FILL, GHOST_ALPHA);
        rect.setStrokeStyle(1, 0x22c55e);
        rect.setDepth(Depth.OVERLAY_CONTENT);
        rect.setAlpha(GHOST_ALPHA);
        this.ghostTiles.set(key, rect);
      }
    }

    // Make ghost cells interactive — on click, find the best anchor for that cell
    for (const anchor of placements) {
      const shapeCells = shape.cells.map(c => ({
        row: c.row + anchor.row,
        col: c.col + anchor.col,
      }));

      for (const sc of shapeCells) {
        const key = cellKey(sc);
        const rect = this.ghostTiles.get(key);
        if (!rect || rect._hasListener) continue;
        rect._hasListener = true;
        rect.setInteractive({ useHandCursor: true });

        rect.on("pointerover", () => {
          // Find the anchor that contains this cell
          this.showPreviewForCell(sc);
        });
        rect.on("pointerout", () => {
          this.clearPreview();
          this.hoveredAnchor = null;
        });
        rect.on("pointerdown", () => {
          if (this.hoveredAnchor) {
            this.confirmPlacement(this.hoveredAnchor);
          }
        });
      }
    }
  }

  private showPreviewForCell(cell: Cell): void {
    this.clearPreview();
    const shape = this.rotations[this.rotationIndex];

    // Find the best anchor: the one where this cell is part of the shape
    // and the placement is valid
    const placements = this.mask.validPlacements(shape, 10);
    let bestAnchor: Cell | null = null;

    for (const anchor of placements) {
      const shapeCells = shape.cells.map(c => ({
        row: c.row + anchor.row,
        col: c.col + anchor.col,
      }));
      if (shapeCells.some(sc => sc.row === cell.row && sc.col === cell.col)) {
        bestAnchor = anchor;
        break;
      }
    }

    if (!bestAnchor) return;
    this.hoveredAnchor = bestAnchor;

    const shapeCells = shape.cells.map(c => ({
      row: c.row + bestAnchor!.row,
      col: c.col + bestAnchor!.col,
    }));

    for (const sc of shapeCells) {
      const { x, y } = this.cellToPixel(sc);
      const rect = this.scene.add.rectangle(x, y, this.tileSize, this.tileSize, PREVIEW_FILL, PREVIEW_ALPHA);
      rect.setStrokeStyle(2, 0x4ade80);
      rect.setDepth(Depth.OVERLAY_CONTENT + 1);
      this.previewTiles.push(rect);
    }
  }

  private clearPreview(): void {
    for (const t of this.previewTiles) t.destroy();
    this.previewTiles = [];
  }

  private clearGhosts(): void {
    for (const rect of this.ghostTiles.values()) {
      rect.removeInteractive();
      rect.removeAllListeners();
      rect.destroy();
    }
    this.ghostTiles.clear();
  }

  private confirmPlacement(anchor: Cell): void {
    const shape = this.rotations[this.rotationIndex];
    this.mask.placeShape(shape, anchor.row, anchor.col);
    const callback = this.onPlace;
    const mask = this.mask;
    this.destroy();
    callback(mask);
  }

  destroy(): void {
    if (this.keyHandler) {
      window.removeEventListener("keydown", this.keyHandler);
      this.keyHandler = null;
    }
    this.clearPreview();
    this.clearGhosts();
    for (const t of this.existingTiles) t.destroy();
    this.existingTiles = [];
    if (this.panel) {
      this.panel.destroy();
      this.panel = null;
    }
  }
}
